import React, { useEffect, useMemo, useState } from 'react';
import { Modal } from './Modal';
import { AsyncSearchableSelect } from './AsyncSearchableSelect';
import { api, fetchCompanyOptions } from '../lib/api';
import {
  generateForm10IndexPDF,
  exportForm10IndexToExcel,
  type Form10IndexItem,
} from '../lib/pdf-form10-index-client';
import { formatIndexDate } from '../lib/pdf-form11-index-client';

interface Form10ExportIndexModalProps {
  open: boolean;
  onClose: () => void;
  initialCompanyId?: string;
}

type PeriodPreset = 'last1year' | 'last6months' | 'last3months' | 'all' | 'custom';

export function Form10ExportIndexModal({
  open,
  onClose,
  initialCompanyId = '',
}: Form10ExportIndexModalProps): React.ReactNode {
  const [companyId, setCompanyId] = useState('');
  const [companyName, setCompanyName] = useState('');
  const [periodPreset, setPeriodPreset] = useState<PeriodPreset>('last1year');
  const [customFromDate, setCustomFromDate] = useState('');
  const [customToDate, setCustomToDate] = useState('');

  const [records, setRecords] = useState<Form10IndexItem[]>([]);
  const [loadingRecords, setLoadingRecords] = useState(false);
  const [exportingPdf, setExportingPdf] = useState(false);
  const [exportingExcel, setExportingExcel] = useState(false);
  const [error, setError] = useState('');

  // Live PDF preview state
  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewPdfBlobUrl, setPreviewPdfBlobUrl] = useState<string | null>(null);
  const [previewLoading, setPreviewLoading] = useState(false);

  // Complete reset when modal opens or closes
  useEffect(() => {
    if (open) {
      setCompanyId(initialCompanyId || '');
      setCompanyName('');
      setPeriodPreset('last1year');
      setCustomFromDate('');
      setCustomToDate('');
      setRecords([]);
      setError('');
      setPreviewOpen(false);
      setPreviewLoading(false);
      if (previewPdfBlobUrl) {
        URL.revokeObjectURL(previewPdfBlobUrl);
        setPreviewPdfBlobUrl(null);
      }
    } else {
      setCompanyId('');
      setCompanyName('');
      setPeriodPreset('last1year');
      setCustomFromDate('');
      setCustomToDate('');
      setRecords([]);
      setError('');
      setPreviewOpen(false);
      setPreviewLoading(false);
      if (previewPdfBlobUrl) {
        URL.revokeObjectURL(previewPdfBlobUrl);
        setPreviewPdfBlobUrl(null);
      }
    }
  }, [open, initialCompanyId]);

  // Calculate effective date range
  const { fromDate, toDate, periodLabel } = useMemo(() => {
    const today = new Date();
    const toDateStr = today.toISOString().split('T')[0];

    if (periodPreset === 'last1year') {
      const d = new Date(today);
      d.setFullYear(d.getFullYear() - 1);
      return {
        fromDate: d.toISOString().split('T')[0],
        toDate: toDateStr,
        periodLabel: 'Last_1_Year',
      };
    }
    if (periodPreset === 'last6months') {
      const d = new Date(today);
      d.setMonth(d.getMonth() - 6);
      return {
        fromDate: d.toISOString().split('T')[0],
        toDate: toDateStr,
        periodLabel: 'Last_6_Months',
      };
    }
    if (periodPreset === 'last3months') {
      const d = new Date(today);
      d.setMonth(d.getMonth() - 3);
      return {
        fromDate: d.toISOString().split('T')[0],
        toDate: toDateStr,
        periodLabel: 'Last_3_Months',
      };
    }
    if (periodPreset === 'custom') {
      return {
        fromDate: customFromDate,
        toDate: customToDate,
        periodLabel: customFromDate && customToDate ? `${customFromDate}_to_${customToDate}` : 'Custom_Period',
      };
    }
    return {
      fromDate: '',
      toDate: '',
      periodLabel: 'All_Records',
    };
  }, [periodPreset, customFromDate, customToDate]);

  // Fetch company details to get name
  useEffect(() => {
    if (!companyId) {
      setCompanyName('');
      setRecords([]);
      return;
    }

    api.companies
      .get(companyId)
      .then((res: any) => {
        const comp = res.data ?? res;
        setCompanyName(comp.company_name || '');
      })
      .catch(() => {
        setCompanyName('');
      });
  }, [companyId]);

  // Fetch Form 10 records matching company and date range
  useEffect(() => {
    if (!open || !companyId) {
      setRecords([]);
      setLoadingRecords(false);
      return;
    }

    if (periodPreset === 'custom' && (!customFromDate || !customToDate)) {
      setRecords([]);
      setLoadingRecords(false);
      return;
    }

    setLoadingRecords(true);
    setError('');

    const params: Record<string, unknown> = {
      company_id: companyId,
      limit: 10000,
      sortBy: 'examination_date',
      sortOrder: 'asc',
    };

    if (fromDate) params.from_date = fromDate;
    if (toDate) params.to_date = toDate;

    api.form10
      .list(params)
      .then((res: any) => {
        const data = Array.isArray(res.data) ? res.data : res.data?.data ?? [];
        setRecords(data as Form10IndexItem[]);
      })
      .catch((err: unknown) => {
        setError(err instanceof Error ? err.message : 'Failed to fetch Form 10 records');
        setRecords([]);
      })
      .finally(() => {
        setLoadingRecords(false);
      });
  }, [open, companyId, fromDate, toDate, periodPreset, customFromDate, customToDate]);

  // Handle Export to PDF
  const handleExportPdf = () => {
    if (!companyId) {
      setError('Please select a company first.');
      return;
    }
    if (records.length === 0) {
      setError('No inspection records found for the selected company and period.');
      return;
    }

    setExportingPdf(true);
    setError('');

    try {
      const blobUrl = generateForm10IndexPDF({
        companyName,
        items: records,
        periodLabel,
      });

      const cleanCompanyName = (companyName || 'Company').replace(/[^a-zA-Z0-9_-]/g, '_');
      const todayStr = new Date().toISOString().split('T')[0];
      const link = document.createElement('a');
      link.href = blobUrl;
      link.download = `Form10_Index_${cleanCompanyName}_${periodLabel}_${todayStr}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      setTimeout(() => {
        URL.revokeObjectURL(blobUrl);
      }, 5000);

      onClose();
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Failed to generate Form 10 PDF index');
    } finally {
      setExportingPdf(false);
    }
  };

  // Handle Export to Excel
  const handleExportExcel = () => {
    if (!companyId) {
      setError('Please select a company first.');
      return;
    }
    if (records.length === 0) {
      setError('No inspection records found for the selected company and period.');
      return;
    }

    setExportingExcel(true);
    setError('');

    try {
      exportForm10IndexToExcel({
        companyName,
        items: records,
        periodLabel,
      });
      onClose();
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Failed to export Form 10 Excel index');
    } finally {
      setExportingExcel(false);
    }
  };

  // Handle Live PDF Preview
  const handleOpenPreview = () => {
    if (!companyId) {
      setError('Please select a company first.');
      return;
    }
    if (records.length === 0) {
      setError('No inspection records found to preview.');
      return;
    }

    setPreviewLoading(true);
    setPreviewOpen(true);

    setTimeout(() => {
      try {
        if (previewPdfBlobUrl) {
          URL.revokeObjectURL(previewPdfBlobUrl);
        }
        const blobUrl = generateForm10IndexPDF({
          companyName,
          items: records,
          periodLabel,
        });
        setPreviewPdfBlobUrl(blobUrl);
      } catch (err: unknown) {
        setError(err instanceof Error ? err.message : 'Failed to generate PDF preview');
        setPreviewOpen(false);
      } finally {
        setPreviewLoading(false);
      }
    }, 150);
  };

  const handleClosePreview = () => {
    setPreviewOpen(false);
    setPreviewLoading(false);
    if (previewPdfBlobUrl) {
      URL.revokeObjectURL(previewPdfBlobUrl);
      setPreviewPdfBlobUrl(null);
    }
  };

  return (
    <>
      <Modal open={open && !previewOpen} onClose={onClose} title="Export Form 10 Index" size="xl">
        <div className="space-y-4">
          <p className="text-xs text-gray-500">
            Generate and export the Form 10 Lifting Tackle & Equipment Inspection Index document to return to the inspected client company.
          </p>

          {error && (
            <div className="p-3 bg-red-50 border border-red-200 text-red-700 rounded-lg text-xs flex items-center justify-between">
              <span>{error}</span>
              <button
                onClick={() => setError('')}
                className="text-red-600 hover:text-red-800 font-bold ml-2 cursor-pointer"
              >
                ✕
              </button>
            </div>
          )}

          {/* ── 1. Select Company ─────────────────────── */}
          <div className="bg-slate-50 p-3.5 rounded-xl border border-slate-200 space-y-2">
            <label className="block text-xs font-semibold text-slate-700">
              Select Inspected Company <span className="text-red-500">*</span>
            </label>
            <AsyncSearchableSelect
              placeholder="Search and select client company..."
              fetchOptions={fetchCompanyOptions}
              value={companyId}
              onChange={(val) => {
                setCompanyId(val);
                setError('');
              }}
            />
          </div>

          {/* ── 2. Select Inspection Period ───────────── */}
          <div className="bg-slate-50 p-3.5 rounded-xl border border-slate-200 space-y-3">
            <label className="block text-xs font-semibold text-slate-700">
              Inspection Period Filter
            </label>
            <div className="grid grid-cols-2 sm:grid-cols-5 gap-2">
              <button
                type="button"
                onClick={() => setPeriodPreset('last1year')}
                className={`px-3 py-2 text-xs font-semibold rounded-lg border transition-all cursor-pointer text-center ${
                  periodPreset === 'last1year'
                    ? 'bg-brand-600 border-brand-600 text-white shadow-xs'
                    : 'bg-white border-slate-200 text-slate-700 hover:bg-slate-100'
                }`}
              >
                Last 1 Year <span className="text-[10px] block font-normal opacity-85">(Default)</span>
              </button>

              <button
                type="button"
                onClick={() => setPeriodPreset('last6months')}
                className={`px-3 py-2 text-xs font-semibold rounded-lg border transition-all cursor-pointer text-center ${
                  periodPreset === 'last6months'
                    ? 'bg-brand-600 border-brand-600 text-white shadow-xs'
                    : 'bg-white border-slate-200 text-slate-700 hover:bg-slate-100'
                }`}
              >
                Last 6 Months
              </button>

              <button
                type="button"
                onClick={() => setPeriodPreset('last3months')}
                className={`px-3 py-2 text-xs font-semibold rounded-lg border transition-all cursor-pointer text-center ${
                  periodPreset === 'last3months'
                    ? 'bg-brand-600 border-brand-600 text-white shadow-xs'
                    : 'bg-white border-slate-200 text-slate-700 hover:bg-slate-100'
                }`}
              >
                Last 3 Months
              </button>

              <button
                type="button"
                onClick={() => setPeriodPreset('all')}
                className={`px-3 py-2 text-xs font-semibold rounded-lg border transition-all cursor-pointer text-center ${
                  periodPreset === 'all'
                    ? 'bg-brand-600 border-brand-600 text-white shadow-xs'
                    : 'bg-white border-slate-200 text-slate-700 hover:bg-slate-100'
                }`}
              >
                All Records
              </button>

              <button
                type="button"
                onClick={() => setPeriodPreset('custom')}
                className={`px-3 py-2 text-xs font-semibold rounded-lg border transition-all cursor-pointer text-center ${
                  periodPreset === 'custom'
                    ? 'bg-brand-600 border-brand-600 text-white shadow-xs'
                    : 'bg-white border-slate-200 text-slate-700 hover:bg-slate-100'
                }`}
              >
                Custom Range
              </button>
            </div>

            {/* Custom Date Pickers */}
            {periodPreset === 'custom' && (
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 pt-2 border-t border-slate-200">
                <div>
                  <label className="block text-[11px] font-medium text-slate-600 mb-1">
                    From Inspection Date <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="date"
                    value={customFromDate}
                    onChange={(e) => setCustomFromDate(e.target.value)}
                    className="input text-xs w-full"
                  />
                </div>
                <div>
                  <label className="block text-[11px] font-medium text-slate-600 mb-1">
                    To Inspection Date <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="date"
                    value={customToDate}
                    onChange={(e) => setCustomToDate(e.target.value)}
                    className="input text-xs w-full"
                  />
                </div>
              </div>
            )}
          </div>

          {/* ── 3. Matching Records Preview ───────────── */}
          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <span className="text-xs font-bold text-slate-800 flex items-center gap-1.5">
                <span>Index Content Preview</span>
                {companyId && !loadingRecords && (
                  <span className="px-2 py-0.5 rounded-full text-[11px] font-medium bg-slate-100 text-slate-700 border border-slate-200">
                    {records.length} {records.length === 1 ? 'item' : 'items'}
                  </span>
                )}
              </span>
              {companyId && records.length > 0 && (
                <button
                  type="button"
                  onClick={handleOpenPreview}
                  className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-blue-200 bg-blue-50 hover:bg-blue-100 text-blue-700 text-xs font-semibold transition-colors cursor-pointer shadow-xs"
                >
                  <svg className="w-3.5 h-3.5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
                    <circle cx="12" cy="12" r="3" />
                  </svg>
                  <span>Preview PDF</span>
                </button>
              )}
            </div>

            {loadingRecords ? (
              <div className="py-10 text-center text-xs text-slate-500 bg-white rounded-xl border border-slate-200 flex flex-col items-center justify-center space-y-2">
                <div className="w-6 h-6 border-2 border-brand-600 border-t-transparent rounded-full animate-spin" />
                <p className="font-medium text-slate-600">Loading equipment inspection records...</p>
              </div>
            ) : !companyId ? (
              <div className="py-8 text-center text-xs text-slate-400 bg-white rounded-xl border border-dashed border-slate-300 flex flex-col items-center justify-center space-y-1">
                <svg className="w-8 h-8 text-slate-300" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                  <path d="M19 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11l5 5v11a2 2 0 0 1-2 2z" />
                  <polyline points="17 21 17 13 7 13 7 21" />
                  <polyline points="7 3 7 8 15 8" />
                </svg>
                <p>Select a company above to load inspection records.</p>
              </div>
            ) : records.length === 0 ? (
              <div className="py-8 text-center text-xs text-amber-700 bg-amber-50 rounded-xl border border-amber-200 flex flex-col items-center justify-center space-y-1">
                <svg className="w-6 h-6 text-amber-500" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <circle cx="12" cy="12" r="10" />
                  <line x1="12" y1="8" x2="12" y2="12" />
                  <line x1="12" y1="16" x2="12.01" y2="16" />
                </svg>
                <p className="font-medium">No Form 10 inspection reports found for this company in the selected period.</p>
                <p className="text-[11px] text-amber-600">Try changing the period filter above to "All Records" or a wider date range.</p>
              </div>
            ) : (
              <div className="max-h-60 overflow-y-auto border border-slate-200 rounded-xl shadow-xs">
                <table className="min-w-full text-xs text-left">
                  <thead className="bg-amber-100 text-amber-950 font-semibold sticky top-0 border-b border-amber-200">
                    <tr>
                      <th className="py-2.5 px-2 text-center w-10">Sr.</th>
                      <th className="py-2.5 px-2.5">Tag No.</th>
                      <th className="py-2.5 px-2.5">Remark / Size</th>
                      <th className="py-2.5 px-2.5">Description Of Tool</th>
                      <th className="py-2.5 px-2 text-center">SWL</th>
                      <th className="py-2.5 px-2 text-center">Inspection</th>
                      <th className="py-2.5 px-2 text-center">Due Date</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100 bg-white">
                    {records.map((item, idx) => {
                      const isReject = item.defect_status === 'REJECT' || item.defect_status === 'DEFECT_FOUND';
                      const tagNo = item.internal_id_number || item.equipment_short_code || item.certificate_number || '—';
                      
                      let remark = '';
                      if (isReject) {
                        remark = item.equipment_size ? `${item.equipment_size} - Reject` : (item.defect_remarks || 'Reject');
                      } else if (item.equipment_size) {
                        remark = item.equipment_size;
                      } else if (item.make || item.model) {
                        remark = `${item.make || ''} ${item.model || ''}`.trim();
                      }

                      let swl = '—';
                      if (item.swl_text) {
                        swl = item.swl_text;
                      } else if (item.swl_value !== undefined && item.swl_value !== null) {
                        swl = `${item.swl_value} Ton`;
                      }

                      return (
                        <tr key={item.id || idx} className="hover:bg-slate-50 transition-colors">
                          <td className="py-2 px-2 text-center text-slate-500 font-medium">
                            {idx + 1}
                          </td>
                          <td className={`py-2 px-2.5 font-medium ${isReject ? 'text-red-600 font-bold' : 'text-slate-800'}`}>
                            {tagNo}
                          </td>
                          <td className={`py-2 px-2.5 ${isReject ? 'text-red-600 font-bold' : 'text-slate-700'}`}>
                            {remark || '—'}
                          </td>
                          <td className="py-2 px-2.5 text-slate-700">
                            {item.equipment_name_type || '—'}
                          </td>
                          <td className="py-2 px-2 text-center text-slate-700">
                            {swl}
                          </td>
                          <td className="py-2 px-2 text-center text-slate-700 font-mono text-[11px]">
                            {formatIndexDate(item.examination_date) || '—'}
                          </td>
                          <td className="py-2 px-2 text-center text-slate-700 font-mono text-[11px]">
                            {formatIndexDate(item.next_inspection_date) || '—'}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </div>

          {/* ── Footer Actions ────────────────────────── */}
          <div className="flex flex-col-reverse sm:flex-row items-center justify-end gap-2.5 pt-3 border-t border-slate-200">
            <button
              type="button"
              onClick={onClose}
              className="btn btn-secondary text-xs w-full sm:w-auto px-4 py-2"
            >
              Cancel
            </button>

            <button
              type="button"
              onClick={handleExportExcel}
              disabled={!companyId || records.length === 0 || exportingExcel || exportingPdf}
              className="inline-flex items-center justify-center gap-1.5 px-4 py-2 bg-emerald-600 hover:bg-emerald-700 text-white text-xs font-semibold rounded-lg shadow-sm transition-colors disabled:opacity-50 disabled:cursor-not-allowed cursor-pointer w-full sm:w-auto"
            >
              <svg className="w-4 h-4 text-white" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
              <span>{exportingExcel ? 'Exporting Excel...' : 'Export Excel (.xlsx)'}</span>
            </button>

            <button
              type="button"
              onClick={handleExportPdf}
              disabled={!companyId || records.length === 0 || exportingPdf || exportingExcel}
              className="inline-flex items-center justify-center gap-1.5 px-4 py-2 bg-brand-600 hover:bg-brand-700 text-white text-xs font-semibold rounded-lg shadow-sm transition-colors disabled:opacity-50 disabled:cursor-not-allowed cursor-pointer w-full sm:w-auto"
            >
              <svg className="w-4 h-4 text-white" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" strokeLinecap="round" strokeLinejoin="round" />
                <polyline points="14 2 14 8 20 8" strokeLinecap="round" strokeLinejoin="round" />
                <line x1="16" y1="13" x2="8" y2="13" strokeLinecap="round" strokeLinejoin="round" />
                <line x1="16" y1="17" x2="8" y2="17" strokeLinecap="round" strokeLinejoin="round" />
                <polyline points="10 9 9 9 8 9" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
              <span>{exportingPdf ? 'Generating PDF...' : 'Export PDF Index'}</span>
            </button>
          </div>
        </div>
      </Modal>

      {/* ── Live PDF Preview Modal ──────────────────── */}
      {previewOpen && (
        <Modal open={previewOpen} onClose={handleClosePreview} title={`Form 10 Index Preview — ${companyName}`} size="xl">
          <div className="space-y-3">
            {/* Top Toolbar in Preview Modal */}
            <div className="flex flex-col sm:flex-row items-center justify-between gap-2 p-2.5 bg-slate-50 border border-slate-200 rounded-xl">
              <div className="flex items-center gap-2">
                <span className="text-xs font-bold text-slate-800">{companyName}</span>
                <span className="px-2 py-0.5 rounded-full text-[11px] font-medium bg-slate-200 text-slate-700">
                  {records.length} {records.length === 1 ? 'item' : 'items'}
                </span>
              </div>

              <div className="flex items-center gap-2 w-full sm:w-auto justify-end">
                <button
                  type="button"
                  onClick={handleClosePreview}
                  className="btn btn-secondary text-xs py-1.5 px-3"
                >
                  Back to Settings
                </button>

                <button
                  type="button"
                  onClick={handleExportExcel}
                  className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-emerald-600 hover:bg-emerald-700 text-white text-xs font-semibold rounded-lg shadow-sm transition-colors cursor-pointer"
                >
                  <svg className="w-3.5 h-3.5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" strokeLinecap="round" strokeLinejoin="round" />
                  </svg>
                  <span>Export Excel</span>
                </button>

                <button
                  type="button"
                  onClick={handleExportPdf}
                  className="inline-flex items-center gap-1.5 px-3.5 py-1.5 bg-brand-600 hover:bg-brand-700 text-white text-xs font-semibold rounded-lg shadow-sm transition-colors cursor-pointer"
                >
                  <svg className="w-3.5 h-3.5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" strokeLinecap="round" strokeLinejoin="round" />
                    <polyline points="14 2 14 8 20 8" strokeLinecap="round" strokeLinejoin="round" />
                  </svg>
                  <span>Download PDF</span>
                </button>
              </div>
            </div>

            {/* Iframe View */}
            <div className="w-full h-[58vh] bg-slate-100 rounded-xl overflow-hidden border border-slate-300 relative flex items-center justify-center shadow-inner">
              {previewLoading || !previewPdfBlobUrl ? (
                <div className="flex flex-col items-center justify-center space-y-3 text-slate-500 py-16">
                  <div className="w-8 h-8 border-3 border-brand-600 border-t-transparent rounded-full animate-spin" />
                  <p className="text-xs font-semibold text-slate-600">Rendering Form 10 Index PDF...</p>
                </div>
              ) : (
                <iframe
                  src={previewPdfBlobUrl}
                  title="Form 10 Index PDF Preview"
                  className="w-full h-full border-0"
                />
              )}
            </div>
          </div>
        </Modal>
      )}
    </>
  );
}
